import { useState, useEffect } from 'react';
import { SIZE_STATUS_INSTOCK } from 'frontier-constants';

// Return up to 10 total carton sizes.
const maxResults = 10;
// How many “reverse” (smaller) matches to allow.
// (Previously set to 2, but we now want to capture more lower sizes.)
const maxSmallerResults = 4;

export function useCartonSimilarSizes({ cartonDies, width, length, depth, shapeId }) {
    const [sizes, setSizes] = useState([]);

    useEffect(() => {
        // Only run filtering if all values are present.
        if (
            !Array.isArray(cartonDies) ||
            cartonDies.length === 0 ||
            width == null ||
            length == null ||
            depth == null ||
            shapeId == null
        ) {
            setSizes([]);
            return;
        }

        // Pre‑filter the dataset to only include items matching the desired shape
        // and that are in stock.
        const filteredData = cartonDies.filter(
            (item) =>
                item.shape_id === shapeId &&
                parseInt(item.status_id, 10) === SIZE_STATUS_INSTOCK
        );

        // A helper to compare numbers with a small tolerance (to account for floating‐point issues).
        const equals = (a, b) => Math.abs(a - b) < 0.001;

        // Helper function to filter an array of items given criteria for length, width, and depth.
        const filterResults = (data, lengthCriteria, widthCriteria, depthCriteria) =>
            data.filter((item) =>
                lengthCriteria(parseFloat(item.length)) &&
                widthCriteria(parseFloat(item.width)) &&
                depthCriteria(parseFloat(item.depth))
            );

        // Each “step” is 0.125 inches. For example, an increment of 1 adds 0.125″.
        const findNextSize = (value, increments) =>
            Math.round((value + increments * 0.125) * 1000) / 1000;

        // Define the “forward” (non‑reverse) filter sequence.
        // (Each object indicates how much to add to the input length and width.)
        const normalFilterSequence = [
            { dL: 0,     dW: 0 },       // Filter One: exact match on L & W
            { dL: 0.125, dW: 0 },       // Filter Two: length +1/8", same width
            { dL: 0,     dW: 0.125 },   // Filter Three: width +1/8", same length
            { dL: 0.25,  dW: 0 },       // Filter Four: length +1/4"
            { dL: 0,     dW: 0.25 },    // Filter Five: width +1/4"
            { dL: 0.375, dW: 0 },       // Filter Six: length +3/8"
            { dL: 0,     dW: 0.375 }    // Filter Seven: width +3/8"
        ];

        // Define the “reverse” (smaller) filter sequence.
        // (Here we subtract increments to look for cartons with slightly smaller dimensions.)
        // Note that we do not include an exact match because that is handled by the forward filters.
        const reverseFilterSequence = [
            { dL: 0,     dW: 0.125 },
            { dL: 0.125, dW: 0 },
            { dL: 0,     dW: 0.25 },
            { dL: 0.25,  dW: 0 }
        ];

        // applyFilters applies a given filter sequence over the data.
        // In “reverse” mode it uses a range (for example, for a 1/8″ decrement) whereas in
        // forward mode it looks for an exact match on the computed dimension.
        const applyFilters = (data, depthPredicate, sequence, reverse = false) => {
            const results = [];
            const foundIds = new Set();

            sequence.forEach((step) => {
                let nextLength, nextWidth;
                if (reverse) {
                    // In reverse mode subtract the increments.
                    nextLength = findNextSize(length, -(step.dL / 0.125));
                    nextWidth  = findNextSize(width, -(step.dW / 0.125));
                    // For reverse filtering, create a lower bound one step further.
                    const nextStepLength = findNextSize(length, -((step.dL + 0.125) / 0.125));
                    const nextStepWidth  = findNextSize(width, -((step.dW + 0.125) / 0.125));

                    const newResults = filterResults(
                        data,
                        (l) =>
                            step.dL > 0
                                ? (l >= nextStepLength && l <= nextLength)
                                : equals(l, length),
                        (w) =>
                            step.dW > 0
                                ? (w >= nextStepWidth && w <= nextWidth)
                                : equals(w, width),
                        (d) => depthPredicate(d)
                    );
                    newResults.forEach((item) => {
                        if (!foundIds.has(item.id)) {
                            results.push(item);
                            foundIds.add(item.id);
                        }
                    });
                } else {
                    // Forward mode: add increments.
                    nextLength = findNextSize(length, step.dL / 0.125);
                    nextWidth  = findNextSize(width, step.dW / 0.125);
                    const newResults = filterResults(
                        data,
                        (l) => equals(l, nextLength),
                        (w) => equals(w, nextWidth),
                        (d) => depthPredicate(d)
                    );
                    newResults.forEach((item) => {
                        if (!foundIds.has(item.id)) {
                            results.push(item);
                            foundIds.add(item.id);
                        }
                    });
                }
            });
            return results;
        };

        // getFilteredResults wraps applyFilters and—for reverse filtering—limits results
        // to maxSmallerResults; for forward filtering it returns up to maxResults.
        // The depthPredicate lets us restrict items to a particular depth range.
        const getFilteredResults = (data, expandDepth, reverse = false, previousResults = []) => {
            let results = [];
            const previousIds = new Set(previousResults.map((item) => item.id));

            if (reverse) {
                // Try an exact depth match first.
                results = applyFilters(data, (d) => equals(d, depth), reverseFilterSequence, true);
                // If fewer than our target, expand depth (up to 2" more).
                if (results.length < maxSmallerResults) {
                    const expandedResults = applyFilters(
                        data,
                        (d) => d > depth && d <= depth + 2,
                        reverseFilterSequence,
                        true
                    );
                    expandedResults.forEach((item) => {
                        if (!previousIds.has(item.id) && results.length < maxSmallerResults) {
                            results.push(item);
                        }
                    });
                }
                results = results.filter((item) => !previousIds.has(item.id));
            } else {
                results = applyFilters(data, (d) => d >= depth && d <= expandDepth, normalFilterSequence, false);
            }
            // Sort by “distance” (total difference in length and width from the input)
            results.sort((a, b) => {
                const aDistance =
                    Math.abs(parseFloat(a.length) - length) + Math.abs(parseFloat(a.width) - width);
                const bDistance =
                    Math.abs(parseFloat(b.length) - length) + Math.abs(parseFloat(b.width) - width);
                if (aDistance !== bDistance) return aDistance - bDistance;
                // If equal, further sort by length, width, and depth.
                if (!equals(parseFloat(a.length), parseFloat(b.length)))
                    return parseFloat(a.length) - parseFloat(b.length);
                if (!equals(parseFloat(a.width), parseFloat(b.width)))
                    return parseFloat(a.width) - parseFloat(b.width);
                return parseFloat(a.depth) - parseFloat(b.depth);
            });
            return results.slice(0, reverse ? maxSmallerResults : maxResults);
        };

        // 1. Run forward filtering (for sizes that are equal or larger in length/width).
        const forwardResults = getFilteredResults(filteredData, depth + 1, false);
        let combinedResults = [...forwardResults];

        // If fewer than maxResults found, expand the depth range and try again.
        if (combinedResults.length < maxResults) {
            const additionalForward = getFilteredResults(filteredData, depth + 2, false, combinedResults);
            combinedResults = combinedResults.concat(additionalForward);
        }

        // 2. Run reverse filtering to capture “lower” sizes.
        const reverseResults = getFilteredResults(filteredData, depth + 1, true);
        combinedResults = combinedResults.concat(reverseResults);

        // Deduplicate and sort the combined results.
        const uniqueResults = combinedResults.filter(
            (item, index, self) =>
                index === self.findIndex((t) => t.id === item.id)
        );
        uniqueResults.sort((a, b) => {
            const aDistance =
                Math.abs(parseFloat(a.length) - length) + Math.abs(parseFloat(a.width) - width);
            const bDistance =
                Math.abs(parseFloat(b.length) - length) + Math.abs(parseFloat(b.width) - width);
            return aDistance - bDistance;
        });

        setSizes(uniqueResults.slice(0, maxResults));
    }, [cartonDies, length, width, depth, shapeId]);

    return { sizes };
}
